/**
 * PostmarkBudgetCard — Command Centre canary tile.
 * 2026-07-02
 *
 * Answers one question in one glance:
 *   "How many real Postmark emails have we sent in the last 24h,
 *    and is any of them a CI-leak into the founder's live inbox?"
 *
 * Green  → total < 40/day, no CI leak
 * Amber  → total 40-99/day (unusually chatty)
 * Red    → total >= 100/day OR any admin-digest send landed on the
 *          master-admin Workspace address (the exact 2026-07-02 leak
 *          pattern)
 *
 * Auto-refreshes every 60s. Reuses apiClient auth flow.
 */
import React, { useCallback, useState } from 'react';
import { Send, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAdminHealthCache } from '../../hooks/useAdminHealthCache';

const LIGHT_STYLES = {
  green: {
    border: 'border-emerald-300',
    ring: 'ring-emerald-200/40',
    dot: 'bg-emerald-500',
    label: 'HEALTHY',
    labelClass: 'text-emerald-700',
    banner: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    bannerIcon: 'text-emerald-600',
  },
  amber: {
    border: 'border-amber-400',
    ring: 'ring-amber-200/40',
    dot: 'bg-amber-500',
    label: 'CHATTY',
    labelClass: 'text-amber-800',
    banner: 'border-amber-300 bg-amber-50 text-amber-900',
    bannerIcon: 'text-amber-600',
  },
  red: {
    border: 'border-rose-500',
    ring: 'ring-rose-200/40',
    dot: 'bg-rose-600',
    label: 'LEAK DETECTED',
    labelClass: 'text-rose-800',
    banner: 'border-rose-300 bg-rose-50 text-rose-900',
    bannerIcon: 'text-rose-600',
  },
};

function timeAgo(iso) {
  if (!iso) return '—';
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch (_) { return '—'; }
}

const PostmarkBudgetCard = () => {
  // useAdminHealthCache: shared cache across every admin tile consuming
  // this endpoint. See /app/frontend/src/hooks/useAdminHealthCache.js.
  const { data, loading, error, refetch } = useAdminHealthCache(
    '/api/admin/postmark/budget',
    { pollMs: 60_000 },
  );
  const err = error?.message || null;
  const [refetching, setRefetching] = useState(false);

  const fetchData = useCallback(async () => {
    setRefetching(true);
    try { await refetch(); } finally { setRefetching(false); }
  }, [refetch]);

  const light = LIGHT_STYLES[data?.traffic_light] || LIGHT_STYLES.green;
  const leak = data?.leak_canary;

  return (
    <div
      data-testid="postmark-budget-card"
      className={`rounded-2xl bg-white ${light.border} border-2 ring-1 ${light.ring} p-4 shadow-sm`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4 text-slate-800" strokeWidth={1.7} />
          <h3 className="text-sm font-bold text-slate-900 tracking-wide">
            Postmark Budget · 24h
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest ${light.labelClass}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${light.dot} animate-pulse`} />
            {light.label}
          </span>
          <button
            data-testid="postmark-budget-refresh"
            onClick={fetchData}
            disabled={refetching}
            className="p-1 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refetching ? 'animate-spin' : ''}`} strokeWidth={1.7} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-slate-600 py-4 text-center">Loading…</div>
      ) : err ? (
        <div className="text-xs text-rose-700 py-2" data-testid="postmark-budget-error">{err}</div>
      ) : (
        <>
          <div className="flex items-baseline gap-3 mb-3">
            <div
              data-testid="postmark-budget-total"
              className="text-3xl font-black text-slate-900 tabular-nums leading-none"
            >
              {data.total}
            </div>
            <div className="text-[11px] text-slate-700 uppercase tracking-wider font-semibold">
              sends · {data.failed > 0 ? (
                <span className="text-amber-700">{data.failed} failed</span>
              ) : (
                <span className="text-emerald-700">0 failed</span>
              )}
            </div>
          </div>

          {leak?.detected ? (
            <div
              data-testid="postmark-budget-leak-alert"
              className={`mb-3 rounded-lg border ${light.banner} px-3 py-2 flex items-start gap-2`}
            >
              <AlertTriangle className={`w-4 h-4 ${light.bannerIcon} mt-0.5 shrink-0`} strokeWidth={2} />
              <div className="text-[11px] leading-snug">
                <span className="font-semibold">CI leak: </span>
                {leak.count} admin-digest email{leak.count === 1 ? '' : 's'} delivered to
                <span className="font-mono"> {leak.watched_addresses.join(' / ')} </span>
                in the last 24h. This is the 2026-07-02 spam-bug pattern.
              </div>
            </div>
          ) : (
            <div className={`mb-3 rounded-lg border ${light.banner} px-3 py-1.5 flex items-center gap-2`}>
              <CheckCircle2 className={`w-3.5 h-3.5 ${light.bannerIcon} shrink-0`} strokeWidth={2} />
              <div className="text-[11px] font-medium">
                No CI leaks in the last 24h.
              </div>
            </div>
          )}

          <div className="border-t border-slate-200 pt-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-700 font-bold mb-1.5">
              Top tags
            </div>
            {data.by_tag.length === 0 ? (
              <div className="text-xs text-slate-600 italic py-1">No sends in window.</div>
            ) : (
              <ul data-testid="postmark-budget-tags" className="space-y-1">
                {data.by_tag.slice(0, 6).map((row) => (
                  <li
                    key={row.tag}
                    data-testid={`postmark-tag-${row.tag}`}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="font-mono text-slate-800 truncate max-w-[65%]">{row.tag}</span>
                    <span className="tabular-nums text-slate-700">
                      <span className="text-slate-900 font-bold">{row.count}</span>
                      <span className="text-slate-500"> · {timeAgo(row.last_sent_at)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-2 text-[10px] text-slate-600 font-medium">
            Refresh every 60s · thresholds amber ≥ {data.thresholds.amber_at} · red ≥ {data.thresholds.red_at}
          </div>
        </>
      )}
    </div>
  );
};

export default PostmarkBudgetCard;
